import { useState, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToastStore } from '../../store/toastStore';
import { scheduleAPI } from '../../api/client';
import { track } from '../../utils/analytics';
import useSnapshotData from '../../hooks/useSnapshotData';
import BusSeatLayout from '../BusSeatLayout';
import BottomSheetSelect from '../BottomSheetSelect';
import BottomSheet from '../BottomSheet';
import { COMMERCE_ENABLED } from '../../config/features';

const MIN_SEAT_COUNT = 5;
const ADD_SEAT_COUNT = 3;

const DAY_FULL = {
  monday: '월요일', tuesday: '화요일', wednesday: '수요일',
  thursday: '목요일', friday: '금요일',
};

// 진행률 바
function ProgressBar({ current, total }) {
  const pct = Math.round((current / total) * 100);
  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs text-sub-text font-medium">
          {current}/{total} 단계
        </span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-tayo rounded-full transition-all duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/**
 * 스케줄 개별 등록/수정 풀스크린 바텀시트
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {function} props.onClose
 * @param {string} props.day - 요일 (monday~friday)
 * @param {string} props.direction - 'UP' | 'DOWN'
 * @param {Object} [props.editSchedule] - 수정 모드 시 기존 스케줄
 * @param {string} props.userId
 */
export default function ScheduleSheet({ isOpen, onClose, day, direction, editSchedule, defaultSeatPreferences = [], userId }) {
  const showToast = useToastStore((s) => s.showToast);
  const queryClient = useQueryClient();

  const isEdit = !!editSchedule;

  // 폼 상태
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    lineGroupSeq: '', routeName: '',
    busTime: '', busSeq: '',
    stopName: '', stopSeq: '',
  });
  const [seatWarning, setSeatWarning] = useState(null); // 좌석 선점 안내

  // 수정 모드 초기화
  useEffect(() => {
    if (isOpen && editSchedule) {
      setFormData({
        lineGroupSeq: editSchedule.line_group_seq || '',
        routeName: editSchedule.route_name || '',
        busTime: editSchedule.bus_time || '',
        busSeq: editSchedule.bus_seq || '',
        stopName: editSchedule.stop_name || '',
        stopSeq: editSchedule.stop_seq || '',
      });
      setSeatWarning(null);
      setCurrentStep(3); // 확인 화면에서 시작
    } else if (isOpen) {
      setFormData({
        lineGroupSeq: '', routeName: '',
        busTime: '', busSeq: '',
        stopName: '', stopSeq: '',
      });
      setSeatWarning(null);
      setCurrentStep(1);
    }
  }, [isOpen, editSchedule]);

  // 스냅샷 & 노선 데이터
  const { routes, timetable, stops, isLoading: loadingSnapshot } = useSnapshotData({
    dayOfWeek: day,
    direction,
    lineGroupSeq: formData.lineGroupSeq,
  });

  // 좌석 (로컬 state — 수정: 해당 스케줄 값, 새 등록: 가장 최근 스케줄 값)
  const [seatPreferences, setSeatPreferences] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    if (editSchedule?.seat_preferences?.length) {
      setSeatPreferences(editSchedule.seat_preferences);
    } else {
      setSeatPreferences(defaultSeatPreferences);
    }
  }, [isOpen, editSchedule, defaultSeatPreferences]);

  const handleSeatClick = (seatNumber) => {
    setSeatPreferences(prev =>
      prev.includes(seatNumber) ? prev.filter(s => s !== seatNumber) : [...prev, seatNumber]
    );
  };

  const clearAll = () => setSeatPreferences([]);

  // 저장
  const saveMutation = useMutation({
    mutationFn: async (data) => {
      const result = isEdit
        ? await scheduleAPI.update(editSchedule.id, data)
        : await scheduleAPI.create(data);
      if (!result.success) {
        const err = new Error(result.error || '저장 실패');
        err.needMore = result.needMore;
        err.totalAllocated = result.totalAllocated;
        throw err;
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules', userId] });
      showToast(isEdit ? '스케줄이 수정되었습니다' : '스케줄이 등록되었습니다', 'success');
      track(isEdit ? 'schedule-updated' : 'schedule-created', { direction, day });
      onClose();
    },
    onError: (err) => {
      if (err.message === 'subscription_required') {
        showToast(COMMERCE_ENABLED ? '타요패스가 필요합니다' : '이용 권한이 없습니다', 'error');
        onClose();
        return;
      }
      if (err.needMore) {
        setSeatWarning({
          message: '선택한 좌석이 모두 인기가 많아요. 추가로 3개 이상 좌석을 선택해주세요.',
          totalAllocated: err.totalAllocated,
        });
      } else {
        showToast(err.message || '저장 실패', 'error');
      }
    },
  });

  const handleSubmit = () => {
    if (seatPreferences.length < MIN_SEAT_COUNT) {
      showToast(`선호 좌석을 ${MIN_SEAT_COUNT}개 이상 선택해주세요`, 'error');
      return;
    }
    saveMutation.mutate({
      userId,
      dayOfWeek: day,
      direction,
      routeName: formData.routeName,
      lineGroupSeq: formData.lineGroupSeq,
      busTime: formData.busTime,
      busSeq: formData.busSeq,
      stopName: formData.stopName,
      stopSeq: formData.stopSeq,
      seatPreferences,
    });
  };

  // 핸들러
  const handleRouteChange = (e) => {
    const selected = routes.find(r => r.seq === e.target.value);
    if (selected) {
      setFormData(prev => ({
        ...prev,
        lineGroupSeq: selected.seq, routeName: selected.name,
        busTime: '', busSeq: '', stopName: '', stopSeq: '',
      }));
    }
  };

  const handleBusChange = (time) => {
    const bus = timetable.find(b => b.time === time);
    if (bus) setFormData(prev => ({ ...prev, busTime: time, busSeq: bus.busSeq }));
  };

  const handleStopChange = (e) => {
    const selected = stops.find(s => s.seq.toString() === e.target.value);
    if (selected) setFormData(prev => ({ ...prev, stopSeq: selected.seq.toString(), stopName: selected.name }));
  };

  // 스텝별 진행 조건
  const minRequired = seatWarning ? seatPreferences.length + ADD_SEAT_COUNT : MIN_SEAT_COUNT;
  const canProceed = {
    1: !!formData.lineGroupSeq,
    2: !!formData.busTime && !!formData.stopSeq,
    3: seatPreferences.length >= minRequired,
  };

  const dirLabel = direction === 'UP' ? '등교' : '하교';
  const dirColor = direction === 'UP' ? 'tayo' : 'orange-600';
  const dirBg = direction === 'UP' ? 'tayo-light' : 'orange-50';

  // onBackPress for multi-step navigation
  const handleBackPress = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => Math.max(1, prev - 1));
      return false; // Don't close the sheet
    }
    return true; // Close the sheet
  }, [currentStep]);

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      onBackPress={handleBackPress}
      minHeight="50vh"
      maxHeight="92vh"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 pb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-bold text-charcoal">
            {isEdit ? '스케줄 수정' : '스케줄 등록'}
          </h3>
          <span className={`text-xs font-bold px-2 py-0.5 rounded bg-${dirBg} text-${dirColor}`}>
            {DAY_FULL[day]} {dirLabel}
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 text-sub-text hover:text-charcoal hover:bg-gray-100 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Progress */}
      <div className="px-5">
        <ProgressBar current={currentStep} total={3} />
      </div>

      {/* Content */}
      <div className="overflow-y-auto flex-1 px-5 pb-6 pb-[env(safe-area-inset-bottom)]">

        {/* ============ Step 1: 노선 선택 ============ */}
        {currentStep === 1 && (
          <div className="animate-fade-up space-y-5">
            <h2 className="text-xl font-bold text-charcoal">어디로 가시나요?</h2>

            <div>
              <label className="block text-sm font-semibold text-charcoal mb-3">노선</label>
              {loadingSnapshot ? (
                <div className="bg-white rounded-xl p-4 text-center">
                  <div className="inline-block w-5 h-5 border-2 border-tayo border-t-transparent rounded-full animate-spin mr-2" />
                  <span className="text-sm text-sub-text">노선 정보 불러오는 중...</span>
                </div>
              ) : routes.length === 0 ? (
                <div className="bg-white rounded-xl p-4 text-center">
                  <p className="text-sm text-sub-text">노선 데이터가 없습니다</p>
                </div>
              ) : (
                <BottomSheetSelect
                  value={formData.lineGroupSeq}
                  onChange={handleRouteChange}
                  options={routes.map(r => ({ value: r.seq, label: r.name, sublabel: `${r.busCnt}대` }))}
                  placeholder="노선을 선택하세요"
                  title={`${dirLabel} 노선 선택`}
                  searchable={routes.length > 5}
                />
              )}
            </div>

            <button
              type="button"
              disabled={!canProceed[1]}
              onClick={() => setCurrentStep(2)}
              className="w-full py-4 rounded-xl text-white font-bold text-base transition-all duration-200 btn-press bg-gradient-to-r from-tayo to-tayo-dark disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed shadow-lg shadow-tayo/20 disabled:shadow-none"
            >
              다음
            </button>
          </div>
        )}

        {/* ============ Step 2: 시간 + 정류장 ============ */}
        {currentStep === 2 && (
          <div className="animate-fade-up space-y-5">
            <h2 className="text-xl font-bold text-charcoal">언제 어디서 타시나요?</h2>

            {/* 시간 */}
            <div>
              <label className="block text-sm font-semibold text-charcoal mb-3">버스 시간</label>
              {timetable.length === 0 ? (
                <div className="bg-white rounded-xl p-4 text-center">
                  <p className="text-sm text-sub-text">시간표 데이터가 없습니다</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[32vh] overflow-y-auto pr-1">
                  {timetable.map((bus, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleBusChange(bus.time)}
                      className={`w-full py-3.5 px-4 rounded-xl text-left transition-all duration-200 border-2 btn-press ${
                        formData.busTime === bus.time
                          ? 'bg-tayo-light border-tayo text-tayo font-bold'
                          : 'bg-white border-gray-100 text-charcoal hover:border-gray-200'
                      }`}
                    >
                      <span className="text-base font-semibold">{bus.time}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* 정류장 */}
            {stops.length > 0 && (
              <div>
                <label className="block text-sm font-semibold text-charcoal mb-3">정류장</label>
                <BottomSheetSelect
                  value={formData.stopSeq}
                  onChange={handleStopChange}
                  options={stops.map(s => ({ value: s.seq, label: s.name }))}
                  placeholder="정류장을 선택하세요"
                  title={`${dirLabel} 정류장 선택`}
                  searchable={stops.length > 5}
                />
              </div>
            )}

            <button
              type="button"
              disabled={!canProceed[2]}
              onClick={() => setCurrentStep(3)}
              className="w-full py-4 rounded-xl text-white font-bold text-base transition-all duration-200 btn-press bg-gradient-to-r from-tayo to-tayo-dark disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed shadow-lg shadow-tayo/20 disabled:shadow-none"
            >
              다음
            </button>
          </div>
        )}

        {/* ============ Step 3: 좌석 + 확인 ============ */}
        {currentStep === 3 && (
          <div className="animate-fade-up space-y-5">
            <h2 className="text-xl font-bold text-charcoal">좌석과 설정을 확인해주세요</h2>

            {/* 좌석 선점 경고 */}
            {seatWarning && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 animate-fade-up">
                <p className="text-sm font-semibold text-amber-800">{seatWarning.message}</p>
                <p className="text-xs text-amber-600 mt-1">
                  예약 성공률을 높이려면 넉넉하게 선택해주세요.
                </p>
              </div>
            )}

            {/* 혼잡도 안내 */}
            {seatWarning?.totalAllocated >= 30 && (
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-3">
                <p className="text-xs font-medium text-rose-700">
                  이 버스는 예약 경쟁이 치열해요 ({seatWarning.totalAllocated}/44석)
                </p>
              </div>
            )}

            {/* 좌석 */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold text-charcoal">
                  선호 좌석
                  <span className="text-xs text-sub-text font-normal ml-1.5">
                    ({seatPreferences.length}개 선택 · 최소 {minRequired}개)
                  </span>
                </label>
                {seatPreferences.length > 0 && (
                  <button
                    type="button"
                    onClick={() => { clearAll(); setSeatWarning(null); }}
                    className="text-xs text-coral font-medium hover:text-coral/80 transition-colors"
                  >
                    전체 해제
                  </button>
                )}
              </div>
              <BusSeatLayout
                mode="preference"
                seatPreferences={seatPreferences}
                onSeatClick={(seat) => { handleSeatClick(seat); setSeatWarning(null); }}
              />
              {seatPreferences.length > 0 && (
                <p className="mt-3 text-xs text-sub-text text-center">
                  우선순위: {seatPreferences.join(', ')}
                </p>
              )}
              {seatPreferences.length > 0 && seatPreferences.length < minRequired && (
                <p className="mt-2 text-xs text-amber-600 text-center font-medium">
                  {minRequired - seatPreferences.length}개 더 선택해주세요
                </p>
              )}
            </div>

            {/* 요약 카드 */}
            <div className="bg-gray-50 rounded-2xl p-4 space-y-2.5">
              <h3 className="text-sm font-bold text-charcoal mb-2">등록 정보</h3>
              <SummaryRow label="요일" value={DAY_FULL[day] || '-'} />
              <SummaryRow label="방향" value={dirLabel} />
              <SummaryRow label="노선" value={formData.routeName || '-'} onTap={() => setCurrentStep(1)} />
              <SummaryRow label="시간" value={formData.busTime || '-'} onTap={() => setCurrentStep(2)} />
              <SummaryRow label="정류장" value={formData.stopName || '-'} onTap={() => setCurrentStep(2)} />
              <SummaryRow label="선호좌석" value={seatPreferences.length > 0 ? seatPreferences.join(', ') : '-'} />
            </div>

            {/* CTA */}
            <button
              type="button"
              disabled={saveMutation.isPending || !canProceed[3]}
              onClick={handleSubmit}
              className="w-full py-4 rounded-xl text-white font-bold text-base transition-all duration-200 btn-press bg-gradient-to-r from-tayo to-tayo-dark disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed shadow-lg shadow-tayo/20 disabled:shadow-none"
            >
              {saveMutation.isPending ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  저장 중...
                </span>
              ) : isEdit ? '스케줄 수정' : '스케줄 등록'}
            </button>
          </div>
        )}
      </div>
    </BottomSheet>
  );
}

function SummaryRow({ label, value, onTap }) {
  return (
    <button
      type="button"
      onClick={onTap}
      disabled={!onTap}
      className={`flex items-center w-full text-left py-1.5 ${onTap ? 'group cursor-pointer' : ''}`}
    >
      <span className="text-xs text-sub-text w-14 flex-shrink-0">{label}</span>
      <span className="text-sm font-medium text-charcoal flex-1 truncate">{value}</span>
      {onTap && (
        <svg className="w-4 h-4 text-sub-text opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      )}
    </button>
  );
}
